import React from 'react';
import { Flex } from '@withjoy/joykit';
import { Congratulations } from './components';
import { useThanksForRSVPController } from './ThanksForRSVP.controller';
import { TelemetryProvider } from './ThanksForRSVP.telemetry';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { Redirect } from '@react-router';
import { routePaths } from '@apps/admin/route.paths';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useToggleIntercomLauncher } from '@shared/core/intercom';
import { SuccessScreenSwitch } from './components/SuccessScreenSwitch/ScreenSwitch';
import { useNearbyHotelsData } from './hooks/useNearbyHotelsData';

const SUCCESS_BANNER_KEY = 'rsvpsuccess';
export const SUCCESS_HOTEL_RECOMMENDATION_BANNER_KEY = 'rsvpsuccesshotelrecommendation';
const REDIRECT_SOURCE = 'postrsvpredirect';

interface ThanksForRsvpAppProps
  extends Readonly<{
    eventHandle: string;
  }> {}

const ThanksForRsvpApp: React.FC<ThanksForRsvpAppProps> = ({ eventHandle }) => {
  const {
    data,
    isLoading: thanksForRSVPLoading,
    skipToSuccess,
    fromGiftWrap,
    accommodationsCardVisibilityData,
    shouldRedirect,
    shouldRedirToAccommodations,
    shouldRedirToTravel,
    guestInfo,
    eventId,
    shouldSkipAccommodationsPushStep,
    shouldRedirectToRegistryOnDecline,
    customRedirectUrl,
    showRegistry,
  } = useThanksForRSVPController(eventHandle);

  const nearbyHotelsData = useNearbyHotelsData(eventHandle, eventId ?? '');
  const isLoading = !nearbyHotelsData.isResolved || thanksForRSVPLoading;

  const intercomChatEnabled = useFeatureValue('enableIntercomChatPostRsvp').value === 'on';
  const showIntercomChat = intercomChatEnabled && accommodationsCardVisibilityData.isAccommodationsPageEnabled;

  const askGuestForHotelRecommendationsEnabled = useFeatureValue('askGuestForHotelRecommendationsEnabled').value === 'on';

  useToggleIntercomLauncher({ show: showIntercomChat, userInfo: guestInfo });

  if (shouldRedirect && !isLoading) {
    if (customRedirectUrl) {
      window.location.href = customRedirectUrl;
      return null;
    }

    const query = new URLSearchParams({
      utm_source: REDIRECT_SOURCE,
      bannerKey: SUCCESS_BANNER_KEY,
    });

    let targetPage = routePaths.accommodations.path;
    // If user has accomodations, go there.
    if (shouldRedirToAccommodations && !shouldRedirToTravel) {
      targetPage = routePaths.accommodations.path;
      if (askGuestForHotelRecommendationsEnabled) {
        // Show hotel recommendation survey only on accommodations
        query.set('bannerKey', SUCCESS_HOTEL_RECOMMENDATION_BANNER_KEY);
        query.set('guestId', guestInfo.guestId);
      }
      // If user doesn't have accom, but does have travel, go there.
    } else if (shouldRedirToTravel) {
      targetPage = routePaths.travel.path;
    } else if (shouldRedirectToRegistryOnDecline) {
      targetPage = routePaths.registry.path;
    }

    const path = `${targetPage}?${query.toString()}`;
    return <Redirect to={path} />;
  }

  return (
    <TelemetryProvider>
      {isLoading ? (
        <JoyLogoLoader loaderKey="guest-thank-you-rsvp" />
      ) : skipToSuccess ? (
        <SuccessScreenSwitch
          eventId={eventId || ''}
          eventHandle={eventHandle}
          queryData={data}
          fromGiftWrap={fromGiftWrap}
          accommodationsCardVisibilityData={accommodationsCardVisibilityData}
          forceSkipAccommodationsPushStep={shouldSkipAccommodationsPushStep}
          nearbyHotelsData={nearbyHotelsData}
          showRegistryCard={showRegistry}
        />
      ) : (
        <Flex
          paddingTop={['0', null, '40px']}
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Congratulations
            eventHandle={eventHandle}
            queryData={data}
          />
        </Flex>
      )}
    </TelemetryProvider>
  );
};

export default ThanksForRsvpApp;
